import PropTypes from 'prop-types';
import React from 'react';
import { Radio } from 'components/FormFields';
import { LIMITS } from '../../../shared/fieldTypes';
import { recognizeVideoUrl } from '../../../shared/video';
import { useBuilder } from '../../BuilderContext';
import AssetSlot from '../../fields/AssetSlot';
import { IMAGE_SIZES } from '../../fields/FieldPreview';
import ToggleRow from '../../fields/ToggleRow';
import { Block, Divider, Hint, Note, TextAreaField, TextField } from '../parts';

// Properties for the four layout blocks (plan §6 A2): section,
// description, logo / image and video. Props: { field, update(patch),
// setConfig(key, value) }.

export const editorPropTypes = {
  field: PropTypes.object.isRequired,
  update: PropTypes.func.isRequired,
  setConfig: PropTypes.func.isRequired,
};

// An upload / replace area for an image held in `config[configKey]`.
const ImageUpload = ({
  field,
  configKey,
  setConfig,
  width,
  height,
  fit,
  placeholder,
}) => {
  const { assetUrls, uploadAsset, uploading, readOnly } = useBuilder();
  const id = field.config[configKey];
  return (
    <Block>
      <AssetSlot
        url={id != null ? assetUrls[id] || null : null}
        width={width}
        height={height}
        fit={fit}
        placeholder={placeholder}
        alt={field.label || 'Image'}
        busy={uploading}
        onPick={
          readOnly
            ? undefined
            : async (file) => {
                const assetId = await uploadAsset(file);
                if (assetId) setConfig(configKey, assetId);
              }
        }
      />
    </Block>
  );
};

ImageUpload.propTypes = {
  field: PropTypes.object.isRequired,
  configKey: PropTypes.string.isRequired,
  setConfig: PropTypes.func.isRequired,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  height: PropTypes.number.isRequired,
  fit: PropTypes.string.isRequired,
  placeholder: PropTypes.string.isRequired,
};

export const SectionEditor = ({ field, update, setConfig }) => (
  <>
    <TextField
      label="Section title"
      value={field.label}
      maxLength={LIMITS.label}
      onChange={(label) => update({ label })}
    />
    <TextField
      label="Section description"
      value={field.help}
      placeholder="Optional section description"
      maxLength={LIMITS.help}
      onChange={(help) => update({ help })}
    />
    <Divider />
    <ToggleRow
      first
      label="Header image"
      sub="Full-width banner above this section"
      on={!!field.config.banner}
      onChange={(banner) => setConfig('banner', banner)}
    />
    {field.config.banner ? (
      <>
        <ImageUpload
          field={field}
          configKey="banner_asset_id"
          setConfig={setConfig}
          width="100%"
          height={110}
          fit="cover"
          placeholder="Add a header image"
        />
        <Hint>
          In the app it runs edge to edge across the screen — a wide, landscape
          photo works best.
        </Hint>
      </>
    ) : null}
  </>
);

SectionEditor.propTypes = editorPropTypes;

export const DescriptionEditor = ({ field, update, setConfig }) => (
  <>
    <TextField
      label="Heading"
      value={field.label}
      maxLength={LIMITS.label}
      onChange={(label) => update({ label })}
    />
    <TextAreaField
      label="Text"
      rows={5}
      value={field.config.text}
      maxLength={LIMITS.descriptionText}
      onChange={(text) => setConfig('text', text)}
    />
  </>
);

DescriptionEditor.propTypes = editorPropTypes;

const SIZE_OPTIONS = [
  { label: 'Small', value: 'small' },
  { label: 'Medium', value: 'medium' },
  { label: 'Large', value: 'large' },
];

const ALIGN_OPTIONS = [
  { label: 'Left', value: 'left' },
  { label: 'Center', value: 'center' },
  { label: 'Right', value: 'right' },
];

export const ImageEditor = ({ field, update, setConfig }) => {
  const size = IMAGE_SIZES[field.config.size] || IMAGE_SIZES.medium;
  return (
    <>
      <Note icon="image">
        Upload your logo or image. It shows on this form everywhere it appears.
      </Note>
      <ImageUpload
        field={field}
        configKey="asset_id"
        setConfig={setConfig}
        width={Math.min(size.width, 280)}
        height={Math.round(
          (Math.min(size.width, 280) / size.width) * size.height
        )}
        fit="contain"
        placeholder="Add a logo or image"
      />
      <TextField
        label="Caption (optional)"
        value={field.label}
        placeholder="e.g. Track logo"
        maxLength={LIMITS.label}
        onChange={(label) => update({ label })}
      />
      <Block>
        <Radio
          name={`fb-size-${field.id}`}
          label="Size"
          inline
          value={field.config.size}
          options={SIZE_OPTIONS}
          onChange={(e) => setConfig('size', e.target.value)}
        />
      </Block>
      <Block>
        <Radio
          name={`fb-align-${field.id}`}
          label="Placement"
          inline
          value={field.config.align}
          options={ALIGN_OPTIONS}
          onChange={(e) => setConfig('align', e.target.value)}
        />
      </Block>
    </>
  );
};

ImageEditor.propTypes = editorPropTypes;

const PROVIDERS = { youtube: 'YouTube', vimeo: 'Vimeo' };

export const VideoEditor = ({ field, update, setConfig }) => {
  const video = recognizeVideoUrl(field.config.url);
  return (
    <>
      <TextField
        label="Video link"
        value={field.config.url}
        placeholder="https://vimeo.com/… or youtube.com/watch?v=…"
        inputMode="url"
        maxLength={LIMITS.videoUrl}
        onChange={(url) => setConfig('url', url)}
      />
      <Hint>
        {video
          ? `${
              PROVIDERS[video.provider]
            } video recognised — it plays inline in the app.`
          : 'YouTube and Vimeo links are supported. Use this for a walkthrough, rules briefing or welcome message.'}
      </Hint>
      <TextField
        label="Title"
        value={field.label}
        placeholder="e.g. How to enter"
        maxLength={LIMITS.label}
        onChange={(label) => update({ label })}
      />
      <TextField
        label="Caption (optional)"
        value={field.config.caption}
        placeholder="Shown under the player"
        maxLength={LIMITS.videoCaption}
        onChange={(caption) => setConfig('caption', caption)}
      />
    </>
  );
};

VideoEditor.propTypes = editorPropTypes;

export const LAYOUT_EDITORS = {
  section: SectionEditor,
  description: DescriptionEditor,
  image: ImageEditor,
  video: VideoEditor,
};
